import { type FC, useCallback, useMemo } from "react";
import notification from "@app/services/notification";
import { useScannerRegistration, type ScannerHintConfig, type ScannerRegistrationConfig } from "@app/scanner";
import type { StockKanbanContainer, StockKanbanContainerContent } from "../../../../../../api/stockKanbanContainers";
import { resolveStockKanbanSupplyLotScan } from "../../helpers/resolveStockKanbanSupplyLotScan";

const LOT_SCAN_HINT: ScannerHintConfig = {
    stateKey: "stock-kanban-supply.card.scan-lot",
    title: "Сканируйте этикетку партии ТМЦ",
    description: "Найдём партию, проверим остаток и откроем форму добавления.",
    position: { placement: "bottom-left" },
    indicator: { variant: "scanner" },
};

/** Пропсы компонента {@link StockKanbanSupplyScanPlaceholder}. */
interface StockKanbanSupplyScanPlaceholderProps {
    /** Контейнер КАНБАН. */
    container: StockKanbanContainer;
    /** Уже выбранные партии контейнера. */
    contents: StockKanbanContainerContent[];
    /** Включён ли сценарий сканирования партии. */
    enabled: boolean;
    /**
     * Открывает форму создания партии с предзаполнением.
     * @param lotCode код партии
     * @param qty количество отпуска
     * @param spCode код ячейки; `null`, если нужно выбрать вручную или «на полу»
     */
    onCreateContent: (lotCode: number, qty: number, spCode: number | null) => void;
}

/** Регистрирует сценарий сканирования партии в карточке контейнера КАНБАН. */
export const StockKanbanSupplyScanPlaceholder: FC<StockKanbanSupplyScanPlaceholderProps> = ({
    container,
    contents,
    enabled,
    onCreateContent,
}) => {
    const handleScan = useCallback(
        async (barcode: string): Promise<void> => {
            const result = await resolveStockKanbanSupplyLotScan(barcode, container, contents);

            if (result.type === "notFound") {
                notification.warning(`Партия для скана "${barcode}" не найдена.`);
                return;
            }

            if (result.type === "ambiguous") {
                notification.warning(`Для скана "${barcode}" найдено несколько партий. Выберите партию вручную.`);
                return;
            }

            if (result.type === "stockObjMismatch") {
                notification.warning("Партия недоступна для комплектования этого контейнера.");
                return;
            }

            onCreateContent(result.lot.LotCode, result.qty, result.spCode);
        },
        [container, contents, onCreateContent],
    );

    const scannerRegistration = useMemo(
        (): ScannerRegistrationConfig => ({
            enabled,
            priority: "screen",
            captureInEditable: false,
            hint: LOT_SCAN_HINT,
            onScan: handleScan,
            errorSource: "stock.kanbanSupply.card.scanLot",
        }),
        [enabled, handleScan],
    );

    useScannerRegistration(scannerRegistration);

    return null;
};
